/**
 * Tenant Branding Context
 * Provides theme customization based on tenant/organization branding settings
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_BASE } from '../config/api';

const API = API_BASE;

// Default Cerebro Core branding
const DEFAULT_BRANDING = {
  name: 'Cerebro Core',
  logo_url: 'https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png',
  primary_color: '#D4AF37', // Navigate Gold
  secondary_color: '#f59e0b',
  accent_color: '#0d9488',
  heading_color: '#ffffff',
  text_color: '#94a3b8',
  favicon_url: null,
  login_background_url: null,
  powered_by_visible: true
};

const TenantBrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  applyBranding: () => {},
  updateBranding: async () => {},
  resetToDefault: () => {}
});

export function TenantBrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  // Fetch branding when user's organization changes
  useEffect(() => {
    const fetchBranding = async () => {
      const orgId = user?.organization_id;
      
      if (!orgId) {
        setBranding(DEFAULT_BRANDING);
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API}/api/organizations/${orgId}/branding`);
        const data = res.data;
        
        setBranding({
          ...DEFAULT_BRANDING,
          ...data,
          // Ensure powered_by is always visible
          powered_by_visible: true
        });
      } catch (error) {
        console.error('Failed to fetch branding:', error);
        setBranding(DEFAULT_BRANDING);
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, [user?.organization_id]);

  // Apply CSS variables when branding changes
  useEffect(() => {
    applyBranding(branding);
  }, [branding]);

  // Apply branding as CSS custom properties
  const applyBranding = (brandingData) => {
    const root = document.documentElement;
    
    // Set primary color and derived colors
    const primaryColor = brandingData.primary_color || DEFAULT_BRANDING.primary_color;
    root.style.setProperty('--tenant-primary', primaryColor);
    root.style.setProperty('--tenant-primary-hover', adjustBrightness(primaryColor, -15));
    root.style.setProperty('--tenant-primary-light', adjustBrightness(primaryColor, 30));
    root.style.setProperty('--tenant-primary-10', `${primaryColor}1A`); // 10% opacity
    root.style.setProperty('--tenant-primary-20', `${primaryColor}33`); // 20% opacity

    const secondaryColor = brandingData.secondary_color || DEFAULT_BRANDING.secondary_color;
    root.style.setProperty('--tenant-secondary', secondaryColor);
    root.style.setProperty('--tenant-secondary-10', `${secondaryColor}1A`);
    root.style.setProperty('--tenant-secondary-20', `${secondaryColor}33`);

    const accentColor = brandingData.accent_color || DEFAULT_BRANDING.accent_color;
    root.style.setProperty('--tenant-accent', accentColor);
    root.style.setProperty('--tenant-accent-10', `${accentColor}1A`);
    root.style.setProperty('--tenant-accent-20', `${accentColor}33`);

    const headingColor = brandingData.heading_color || DEFAULT_BRANDING.heading_color;
    root.style.setProperty('--tenant-heading', headingColor);

    const textColor = brandingData.text_color || DEFAULT_BRANDING.text_color;
    root.style.setProperty('--tenant-text', textColor);

    // Update favicon if provided
    if (brandingData.favicon_url) {
      updateFavicon(brandingData.favicon_url);
    }
  };

  // Update branding (for admin settings)
  const updateBranding = async (newBranding) => {
    const orgId = user?.organization_id;
    if (!orgId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/organizations/${orgId}/branding`,
        newBranding,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBranding(prev => ({
        ...prev,
        ...newBranding,
        powered_by_visible: true // Cannot be disabled
      }));
      
      return { success: true };
    } catch (error) {
      console.error('Failed to update branding:', error);
      throw error;
    }
  };

  const resetToDefault = () => {
    setBranding(DEFAULT_BRANDING);
    applyBranding(DEFAULT_BRANDING);
  };

  return (
    <TenantBrandingContext.Provider value={{
      branding,
      loading,
      applyBranding,
      updateBranding,
      resetToDefault
    }}>
      {children}
    </TenantBrandingContext.Provider>
  );
}

export function useTenantBranding() {
  return useContext(TenantBrandingContext);
}

// Helper: Adjust color brightness
function adjustBrightness(hex, percent) {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Adjust brightness
  const adjust = (val) => {
    const adjusted = val + Math.round((percent / 100) * 255);
    return Math.max(0, Math.min(255, adjusted));
  };
  
  const newR = adjust(r).toString(16).padStart(2, '0');
  const newG = adjust(g).toString(16).padStart(2, '0');
  const newB = adjust(b).toString(16).padStart(2, '0');
  
  return `#${newR}${newG}${newB}`;
}

// Helper: Update favicon
function updateFavicon(url) {
  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

export default TenantBrandingContext;
